import React, { useState, useEffect } from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { InboxStats } from "../../lib/inboxLib";

interface ActivityCardProps {
  stats: InboxStats | null;
  period: string;
  updatePeriod: (newPeriod: string) => void;
}

// Helper function to get CSS custom properties
const getChartColor = (propertyName: string): string => {
  // Try body first since that's where the dark class is applied
  const bodyValue = getComputedStyle(document.body)
    .getPropertyValue(propertyName)
    .trim();

  const rootValue = getComputedStyle(document.documentElement)
    .getPropertyValue(propertyName)
    .trim();

  const value = bodyValue || rootValue;

  // console.log(`Getting ${propertyName}:`, {
  //   bodyValue,
  //   rootValue,
  //   finalValue: value,
  //   bodyHasDark: document.body.classList.contains("dark"),
  // });

  return value;
};

// Hook to detect theme changes
const useTheme = () => {
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const checkTheme = () => {
      const darkMode = document.body.classList.contains("dark");
      setIsDark(darkMode);
    };

    // Check initial theme
    checkTheme();

    // Set up observer to watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
};

const barAnimationDuration = 500;
const ActivityCard: React.FC<ActivityCardProps> = ({
  stats,
  period,
  updatePeriod,
}) => {
  const _isDark = useTheme();
  const [isAnimatingDown, setIsAnimatingDown] = useState(false);
  const [displayedStats, setDisplayedStats] = useState<InboxStats | null>(
    stats
  );
  const [displayedPeriod, setDisplayedPeriod] = useState(period);
  const [shouldShowNewData, setShouldShowNewData] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const [pendingPeriod, setPendingPeriod] = useState<string | null>(null);
  const [downAnimationStartTime, setDownAnimationStartTime] = useState<
    number | null
  >(null);

  // Handle period changes with animation
  const handlePeriodChange = (newPeriod: string) => {
    // Don't do anything if clicking the current period
    if (newPeriod === period) {
      return;
    }

    const startTime = Date.now();
    setDownAnimationStartTime(startTime);
    setIsAnimatingDown(true);
    setShouldShowNewData(false);
    setShouldAnimate(true); // Enable animation for the down phase
    setPendingPeriod(newPeriod); // Track which period we're waiting for
    // Wait for animation down to complete before updating period
    setTimeout(() => {
      updatePeriod(newPeriod);
    }, barAnimationDuration);
  };

  // Update displayed data when stats or period change
  useEffect(() => {
    // If we're waiting for new data (animating down) and stats have changed
    // and the period matches what we're waiting for, show the new data
    if (
      !shouldShowNewData &&
      pendingPeriod !== null &&
      stats &&
      period === pendingPeriod &&
      stats !== displayedStats
    ) {
      // We have new stats for the period we're waiting for
      setDisplayedStats(stats);
      setDisplayedPeriod(period);
      setPendingPeriod(null); // Clear pending period

      // Calculate how long to wait to ensure down animation completes
      const elapsed = downAnimationStartTime
        ? Date.now() - downAnimationStartTime
        : 0;
      const remainingWaitTime = Math.max(0, barAnimationDuration - elapsed);

      // Animate up after ensuring the down animation has completed
      setTimeout(() => {
        setDownAnimationStartTime(null); // Clear the start time
        setShouldShowNewData(true);
        setIsAnimatingDown(false);
        setShouldAnimate(true); // Enable animation for the new data
        // Disable animation after it completes
        setTimeout(() => {
          setShouldAnimate(false);
        }, barAnimationDuration);
      }, remainingWaitTime + 400); // Add small buffer to ensure state updates are processed
    } else if (stats !== displayedStats && shouldShowNewData) {
      // Handle initial load or non-period-change updates (like websocket updates during onboarding)
      // Only update if we're not in the middle of a period change animation
      if (period === displayedPeriod) {
        console.log(
          `ActivityCard: Stats updated - period: ${period}, displayedPeriod: ${displayedPeriod}, isInitialLoad: ${isInitialLoad}`
        );
        setDisplayedStats(stats);

        // Mark that we're no longer in initial load state
        if (isInitialLoad) {
          setIsInitialLoad(false);
          // Keep animation enabled for initial load
        } else {
          // Disable animation for non-period-change updates
          setShouldAnimate(false);
        }
      }
    }
  }, [
    stats,
    period,
    displayedPeriod,
    displayedStats,
    shouldShowNewData,
    isInitialLoad,
    pendingPeriod,
    downAnimationStartTime,
  ]);

  // Handle period changes from props (like when FirstLoginModal calls updatePeriod)
  // Only update immediately if we're not in the middle of an animation transition
  useEffect(() => {
    if (
      period !== displayedPeriod &&
      !isAnimatingDown &&
      shouldShowNewData &&
      pendingPeriod === null
    ) {
      console.log(
        `ActivityCard: Period changed from ${displayedPeriod} to ${period}`
      );
      setDisplayedPeriod(period);
      setShouldShowNewData(true);
      setIsAnimatingDown(false);
    }
  }, [
    period,
    displayedPeriod,
    isAnimatingDown,
    shouldShowNewData,
    pendingPeriod,
  ]);

  const renderChart = () => {
    if (!displayedStats || !displayedStats.statsHistory) return null;

    // Get theme-responsive colors
    const chartTextColor = getChartColor("--chart-text-color");
    const chartLineColor = getChartColor("--chart-line-color");
    const goodEmailColor = getChartColor("--chart-good-email-color");
    const badEmailColor = getChartColor("--chart-bad-email-color");
    const notScannedColor = getChartColor("--chart-not-scanned-color");

    // Format data for Recharts
    const chartData = displayedStats.statsHistory.map((day: any) => {
      // Ensure we preserve the date string format for consistent animation
      return {
        date: day.date,
        period: day.period,
        messagesReceived: day.messagesReceived,
        messagesAllowed: day.messagesAllowed,
        messagesFiltered: day.messagesFiltered,
        notScanned: Math.max(
          0,
          day.messagesReceived - (day.messagesAllowed + day.messagesFiltered)
        ),
      };
    });

    // When animating down, preserve the date and period but zero out the values
    const currentData =
      isAnimatingDown || !shouldShowNewData
        ? chartData.map((d) => ({
            ...d,
            messagesAllowed: 0,
            messagesFiltered: 0,
            notScanned: 0,
          }))
        : chartData;

    // Format x-axis labels based on period
    const formatXAxis = (dateStr: string) => {
      if (isAnimatingDown || !shouldShowNewData) return "";

      const [year, month, day] = dateStr.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      switch (displayedPeriod) {
        case "1W":
          return date.toLocaleDateString("en-US", { weekday: "short" });
        case "1M":
          return date.getDate().toString();
        case "1Y":
          return date.toLocaleDateString("en-US", { month: "short" });
        case "all":
          if (displayedStats.statsHistoryGroupingInterval === "year") {
            return date.getFullYear().toString();
          }
          return date.toLocaleDateString("en-US", { month: "short" });
        default:
          return dateStr;
      }
    };

    // Custom X-Axis tick component
    const CustomXAxisTick = (props: any) => {
      const { x, y, payload } = props;

      if (isAnimatingDown || !shouldShowNewData) {
        return null;
      }

      const [year, month, day] = payload.value.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      const isMonthBreak = date.getDate() === 1 && displayedPeriod === "1M";
      const isYearBreak = date.getMonth() === 0 && displayedPeriod === "1Y";
      const showMonthLabel = displayedPeriod === "1M" && isMonthBreak;
      const showYearLabel = displayedPeriod === "1Y" && isYearBreak;

      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={displayedPeriod === "1M" ? 10 : 15}
            textAnchor="middle"
            fill={chartTextColor}
            className="text-sm"
            style={{
              fontSize: displayedPeriod === "1M" ? ".6em" : "1em",
            }}
          >
            {formatXAxis(payload.value)}
          </text>
          {isMonthBreak && (
            <line
              x1={0}
              y1={-8}
              x2={0}
              y2={0}
              stroke={chartLineColor}
              strokeWidth={2}
            />
          )}
          {(showMonthLabel || showYearLabel) && (
            <text
              x={0}
              y={0}
              dy={32}
              textAnchor="middle"
              fill={chartTextColor}
              className="text-xs font-medium"
            >
              {showMonthLabel &&
                date.toLocaleDateString("en-US", { month: "long" })}
              {showYearLabel && date.getFullYear().toString()}
            </text>
          )}
        </g>
      );
    };

    return (
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={currentData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <XAxis
            dataKey="date"
            tick={<CustomXAxisTick />}
            interval={0}
            tickFormatter={formatXAxis}
            height={60}
          />
          <YAxis
            label={{
              angle: -90,
              position: "center",
              style: { textAnchor: "middle" },
            }}
            tick={{
              fill: chartTextColor,
              fontSize: 12,
              opacity: isAnimatingDown || !shouldShowNewData ? 0 : 1,
            }}
          />
          <Tooltip
            labelFormatter={(label) => {
              try {
                const [year, month, day] = label.split("-").map(Number);
                const date = new Date(year, month - 1, day);

                if (isNaN(date.getTime())) {
                  return label;
                }

                if (displayedPeriod === "1Y" || displayedPeriod === "all") {
                  return date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                  });
                }

                return date.toLocaleDateString("en-US", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });
              } catch (error) {
                console.error("Error formatting tooltip date:", error, label);
                return label;
              }
            }}
            formatter={(value, name) => {
              let color = "#ffffff";
              if (name === "Good emails") {
                color = goodEmailColor;
              } else if (name === "Bad emails") {
                color = badEmailColor;
              } else if (name === "Not Scanned") {
                color = "#d1d5db";
              }
              return [
                <span style={{ color }}>
                  {name}: {value}
                </span>,
              ];
            }}
            contentStyle={{
              backgroundColor: "#1f2937",
              color: "#ffffff",
              border: "1px solid #374151",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
            }}
            labelStyle={{
              color: "#ffffff",
              fontWeight: "bold",
            }}
            itemStyle={{
              color: "#ffffff",
            }}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            wrapperStyle={{ paddingTop: "20px" }}
            payload={[
              {
                value: "Good emails",
                type: "rect",
                color: goodEmailColor,
              },
              {
                value: "Bad emails",
                type: "rect",
                color: badEmailColor,
              },
              {
                value: "Not Scanned",
                type: "rect",
                color: notScannedColor,
              },
            ]}
          />
          <Bar
            dataKey="messagesAllowed"
            name="Good emails"
            stackId="a"
            fill={goodEmailColor}
            animationDuration={shouldAnimate ? barAnimationDuration : 0}
            animationBegin={0}
            isAnimationActive={shouldAnimate}
          />
          <Bar
            dataKey="messagesFiltered"
            name="Bad emails"
            stackId="a"
            fill={badEmailColor}
            animationDuration={shouldAnimate ? barAnimationDuration : 0}
            animationBegin={0}
            isAnimationActive={shouldAnimate}
          />
          <Bar
            dataKey="notScanned"
            name="Not Scanned"
            stackId="a"
            fill={notScannedColor}
            animationDuration={shouldAnimate ? barAnimationDuration : 0}
            animationBegin={0}
            isAnimationActive={shouldAnimate}
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Email Activity</h2>
        <div className="flex border border-gray-300 dark:border-gray-600 rounded-full overflow-hidden bg-gray-50 dark:bg-gray-800">
          <button
            className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
              period === "all"
                ? "bg-blue-600 text-white"
                : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
            } rounded-l-full`}
            onClick={() => handlePeriodChange("all")}
          >
            All time
          </button>
          <button
            className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
              period === "1Y"
                ? "bg-blue-600 text-white"
                : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
            onClick={() => handlePeriodChange("1Y")}
          >
            1Y
          </button>
          <button
            className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
              period === "1M"
                ? "bg-blue-600 text-white"
                : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
            onClick={() => handlePeriodChange("1M")}
          >
            1M
          </button>
          <button
            className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
              period === "1W"
                ? "bg-blue-600 text-white"
                : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
            } rounded-r-full`}
            onClick={() => handlePeriodChange("1W")}
          >
            1W
          </button>
        </div>
      </div>
      {stats ? (
        <div>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-900/20 to-blue-900/5 border border-blue-900/30 rounded-lg p-3">
              <div className="text-sm text-blue-600 dark:text-blue-400 mb-2">
                Emails
              </div>
              <div className="text-4xl font-bold text-gray-600 dark:text-white">
                {stats.messagesReceived.toLocaleString()}
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-900/20 to-green-900/5 border border-green-900/30 rounded-lg p-3">
              <div className="text-sm text-green-600 dark:text-green-400 mb-2">
                Good emails
              </div>
              <div className="text-4xl font-bold text-gray-600 dark:text-white">
                {stats.messagesAllowed.toLocaleString()}
                <span className="text-sm font-normal ml-2">
                  {stats.messagesAllowed + stats.messagesFiltered === 0
                    ? ""
                    : "(" +
                      Math.round(
                        (stats.messagesAllowed /
                          (stats.messagesAllowed + stats.messagesFiltered)) *
                          100
                      ) +
                      "%)"}
                </span>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-900/20 to-red-900/5 border border-red-900/30 rounded-lg p-3">
              <div className="text-sm text-red-600 dark:text-red-400 mb-2">
                Bad emails
              </div>
              <div className="text-4xl font-bold text-gray-600 dark:text-white">
                {stats.messagesFiltered.toLocaleString()}
                <span className="text-sm font-normal ml-2">
                  {stats.messagesAllowed + stats.messagesFiltered === 0
                    ? ""
                    : "(" +
                      Math.round(
                        (stats.messagesFiltered /
                          (stats.messagesAllowed + stats.messagesFiltered)) *
                          100
                      ) +
                      "%)"}
                </span>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/20 to-purple-900/5 border border-purple-900/30 rounded-lg p-3">
              <div className="text-sm text-purple-600 dark:text-purple-400 mb-2">
                Donations
              </div>
              <div className="text-4xl font-bold text-gray-600 dark:text-white">
                ${(stats.donationsReceived?.cents / 100).toLocaleString()}
              </div>
            </div>
          </div>
          {renderChart()}
        </div>
      ) : (
        <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded">
          No activity data available.
        </div>
      )}
    </div>
  );
};

export default ActivityCard;
